class Grid {
  constructor({
    columns,
    rows,
    wallProbability = 0.24,
    random = Math.random,
    spot,
  }) {
    this.columns = columns;
    this.rows = rows;
    this.wallProbability = wallProbability;
    this.random = random;
    this.grid = Array.from(Array(columns), () => new Array(rows));

    this.populate({ spot });
    this.callItemsWithGrid("addNeighbors");
    this.randomizeEndpoints();
  }

  get items() {
    return this.grid.flat();
  }

  getSpot(x, y) {
    return this.grid[x]?.[y] ?? null;
  }

  randomGridSpot({ exclude = [] } = {}) {
    const candidates = this.items.filter(
      (item) => !item.wall && !exclude.includes(item),
    );

    if (candidates.length > 0) {
      return candidates[Math.floor(this.random() * candidates.length)];
    }

    const fallback = this.items.find((item) => !exclude.includes(item)) ?? null;

    if (fallback) {
      fallback.wall = false;
    }

    return fallback;
  }

  setEndpoints({ start, end }) {
    this.items.forEach((item) => {
      item.start = false;
      item.end = false;
    });

    this.start = this.getSpot(start.x, start.y);
    this.end = this.getSpot(end.x, end.y);

    if (!this.start || !this.end) {
      throw new Error("Grid endpoints must be inside bounds.");
    }

    this.start.start = true;
    this.start.wall = false;
    this.end.end = true;
    this.end.wall = false;
  }

  randomizeEndpoints() {
    const start = this.randomGridSpot();
    const end = this.randomGridSpot({ exclude: start ? [start] : [] }) ?? start;

    if (!start || !end) {
      throw new Error("Grid requires at least one cell to create endpoints.");
    }

    this.setEndpoints({
      start: { x: start.x, y: start.y },
      end: { x: end.x, y: end.y },
    });
  }

  resetSearch() {
    this.items.forEach((item) => item.resetSearch());

    this.start.start = true;
    this.start.wall = false;
    this.end.end = true;
    this.end.wall = false;
  }

  populate({ spot } = {}) {
    const SpotModel = spot;

    SpotModel.resetIds();

    for (let x = 0; x < this.grid.length; x++) {
      for (let y = 0; y < this.grid[x].length; y++) {
        this.grid[x][y] = new SpotModel({
          x,
          y,
          wall: this.random() < this.wallProbability,
        });
      }
    }
  }

  callItemsWithGrid(functionName) {
    this.items.forEach((item) => item[functionName](this.grid));
  }
}

export { Grid };
